import Axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import config from 'config';

import { getReduxStore } from 'store/storeHolder';

import { clearAuthSession } from 'utils/clearAuthSession';

const { url, liveUrl } = config.api;

const baseUrl = process.env.APP_ENV === 'production' ? liveUrl : url;

export const instance = Axios.create({
  baseURL: baseUrl,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  },
  responseType: 'json',
  timeout: 60000
});

instance.interceptors.request.use(
  async (axiosConfig: InternalAxiosRequestConfig) => {
    const store = getReduxStore();
    const { userInfo } = store.getState().persistAuth;
    const client_ip = localStorage.getItem('client_ip');
    const { guestInfo } = store.getState().persistGuestAuth;

    axiosConfig.params = {
      ...axiosConfig.params,
      vsgapp: 3001
    };

    if (client_ip) {
      axiosConfig.headers['client-ip'] = client_ip;
    }

    if (userInfo) {
      axiosConfig.params = {
        ...axiosConfig.params,
        clid: userInfo?.clid,
        token: userInfo?.token
      };
    } else if (guestInfo) {
      const isCartEndpoint = axiosConfig.url?.toLowerCase().includes('cart');
      const isInvoiceId = isCartEndpoint && guestInfo.invoice_id;
      axiosConfig.params = {
        ...axiosConfig.params,
        clid: guestInfo?.clid,
        token: guestInfo?.token,
        ...(isInvoiceId ? { invoice_id: guestInfo.invoice_id } : {})
      };
    }

    return axiosConfig;
  }
);

instance.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    const errorStatus = error?.response?.status;
    const store = getReduxStore();
    const { userInfo } = store.getState().persistAuth;
    const { guestInfo } = store.getState().persistGuestAuth;
    const { user } = store.getState().user;
    const requestParams = error?.config?.params ?? {};
    const hadAuthParams = Boolean(requestParams.clid && requestParams.token);
    const shouldClearSession =
      errorStatus === 401 || (errorStatus === 403 && !hadAuthParams);

    if (
      shouldClearSession &&
      (userInfo !== null || guestInfo !== null || user !== null)
    ) {
      clearAuthSession();
    }

    if (error?.response?.status === 500) {
      return Promise.reject(
        new Error('Something went wrong. Please try again.')
      );
    }

    return Promise.reject(error);
  }
);

export const post = (
  meth: string,
  data = {},
  options = {}
): Promise<AxiosResponse> => instance.post(meth, data, options);

export const get = (meth: string, params = {}): Promise<AxiosResponse> =>
  instance.get(meth, { params });

export const del = (
  meth: string,
  params = {},
  options = {}
): Promise<AxiosResponse> => instance.delete(meth, { params, ...options });
